import React from 'react';
import axios, { AxiosError } from 'axios';
import { FormikValues } from 'formik';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';
import BForm from '../../../Tools/Form/BForm';
import { useGetFabcompDeviceTypes } from '../../../../hooks/datarefs';
import { RequestStatus } from '../../../Tools/ModalWithLoader/ModalWithLoader';
import { FabcompConfig, RadioConfig } from './types';

export const FABCOMP_CONFIGURATIONS_ENDPOINT = '/api/boi/params/fabcomp-configurations/';

type FabcompConfigurationFormProps = {
  config: FabcompConfig | null;
  configurations: RadioConfig[];
  setRequestStatus: (value: RequestStatus) => void;
  setErrorMessage: (value: string | null) => void;
  onSaved: () => void;
  onClose: () => void;
};

const toOption = (configuration: RadioConfig) => ({
  label: `${configuration.dataset_code} - ${configuration.name}`,
  value: configuration.id
});

const toDeviceTypeOption = (deviceType: string) => ({ label: deviceType, value: deviceType });

export default function FabcompConfigurationForm({
  config,
  configurations,
  setRequestStatus,
  setErrorMessage,
  onSaved,
  onClose
}: FabcompConfigurationFormProps) {
  const { t } = useTranslation();
  const { data: deviceTypes = [] } = useGetFabcompDeviceTypes();
  const configuration = configurations.find(({ id }) => id === config?.profile);

  const fields = [
    {
      name: 'profile',
      label: t('equipmentConfiguration.configuration'),
      options: { values: configurations.map(toOption) },
      validation: object({
        label: string(),
        value: string().required(t('equipmentConfiguration.configurationRequired'))
      })
        .nonNullable()
        .required(t('equipmentConfiguration.configurationRequired'))
    },
    {
      name: 'device_type',
      label: t('equipmentConfiguration.deviceType'),
      options: { values: deviceTypes.map(toDeviceTypeOption) },
      validation: object({
        label: string(),
        value: string().required(t('equipmentConfiguration.deviceTypeRequired'))
      })
        .nonNullable()
        .required(t('equipmentConfiguration.deviceTypeRequired'))
    },
    {
      name: 'fabcomp_label',
      label: t('equipmentConfiguration.fabcompLabel'),
      validation: string().required(t('equipmentConfiguration.fabcompLabelRequired'))
    }
  ];

  const values = {
    profile: configuration ? toOption(configuration) : null,
    device_type: config ? toDeviceTypeOption(config.device_type) : null,
    fabcomp_label: config?.fabcomp_label ?? ''
  };

  const handleSubmit = (formValues: FormikValues) => {
    setRequestStatus('pending');
    const payload = {
      profile: formValues.profile.value,
      device_type: formValues.device_type.value,
      fabcomp_label: formValues.fabcomp_label
    };
    const request = config
      ? axios.put(`${FABCOMP_CONFIGURATIONS_ENDPOINT}${config.id}/`, {
          ...payload,
          updated_at: config.updated_at
        })
      : axios.post(FABCOMP_CONFIGURATIONS_ENDPOINT, payload);

    return request
      .then(() => {
        setRequestStatus('success');
        onSaved();
        setTimeout(onClose, 1500);
      })
      .catch((error: AxiosError) => {
        const data = error.response?.data;
        setRequestStatus('error');
        setErrorMessage(
          typeof data === 'object' && data !== null
            ? Object.values(data).flat().join(' ')
            : t('anErrorOccurred')
        );
      });
  };

  return (
    <BForm
      inModal
      columns={1}
      values={values}
      fields={fields}
      onSubmit={handleSubmit}
      onCancel={onClose}
    />
  );
}
